import { store } from "@store";
import { editEntity } from "@store/story";
import { getActiveEntityId, getEntityById } from "@store/story/selectors";
import type { ButtonProps } from "../types";

/**
 * Maps each primary button setting to its secondary-slot counterpart. Used to
 * move a single "secondary" button's content/styling into the 2nd slot when
 * the layout is promoted to two buttons. Mirrors the field split in
 * `SECONDARY_PROPS` (see `handleProps.ts`).
 */
const PRIMARY_TO_SECONDARY_KEYS: Record<string, string> = {
  title: "titleSecondary",
  url: "urlSecondary",
  target: "targetSecondary",
  color: "colorSecondary",
  colorHover: "colorHoverSecondary",
  background: "backgroundSecondary",
  backgroundHover: "backgroundHoverSecondary",
  borderColor: "borderColorSecondary",
  borderColorHover: "borderColorHoverSecondary",
  borderWidth: "borderWidthSecondary",
  round: "roundSecondary",
  analyticsClickEvent: "analyticsClickEventSecondary",
  anchorName: "anchorNameSecondary",
  anchorTrigger: "anchorTriggerSecondary",
  selectedLinkType: "selectedLinkTypeSecondary",
  openInPopup: "openInPopupSecondary",
  titleVariable: "titleVariableSecondary",
  urlVariable: "urlVariableSecondary",
};

/**
 * Placeholder primary button seeded into the 1st slot when a button that was
 * added as "secondary" is promoted to the two-button layout. Uses the primary
 * design-system button variables so it matches the theme.
 */
const PLACEHOLDER_PRIMARY: Record<string, unknown> = {
  title: "<p>Add text</p>",
  url: "",
  target: "_blank",
  color: "var(--button-text-color)",
  colorHover: "var(--button-text-hover-color)",
  background: "var(--button-background-color)",
  backgroundHover: "var(--button-background-hover-color)",
  borderColor: "var(--button-background-color)",
  borderColorHover: "var(--button-background-hover-color)",
  selectedLinkType: "hyperlink",
  analyticsClickEvent: "",
};

/**
 * Handles the button "Layout" select (`showSecondaryButton`).
 *
 * For a button that was added from the library as the "secondary" preset
 * (`buttonRole === "secondary"`), switching to the two-button layout moves its
 * existing content/styling into the secondary (2nd) slot and seeds a
 * placeholder primary into the 1st slot — so the secondary button lands in 2nd
 * place instead of staying in 1st. Any other button (or value) just updates
 * `showSecondaryButton` normally.
 */
export const handleButtonLayoutChange = (value: string | number) => {
  const layout = String(value);
  const state = store.getState();
  const id = getActiveEntityId(state);
  if (!id) return;

  const settings = getEntityById(id)(state)?.settings as
    | (ButtonProps & Record<string, unknown>)
    | undefined;

  const shouldPromoteSecondary =
    layout === "two" &&
    settings?.name === "Button" &&
    settings?.buttonRole === "secondary";

  if (!shouldPromoteSecondary || !settings) {
    store.dispatch(editEntity({ data: { showSecondaryButton: layout } }));
    return;
  }

  const data: Record<string, unknown> = {
    showSecondaryButton: "two",
    // Promotion is one-shot — once moved to the 2nd slot it is a normal
    // two-button element, so toggling layout again must not re-swap.
    buttonRole: "primary",
  };

  // Move existing (primary-slot) content + styling into the secondary slot.
  for (const [primaryKey, secondaryKey] of Object.entries(
    PRIMARY_TO_SECONDARY_KEYS,
  )) {
    const current = settings[primaryKey];
    if (current !== undefined) data[secondaryKey] = current;
  }

  // Seed a fresh placeholder primary button into the 1st slot.
  Object.assign(data, PLACEHOLDER_PRIMARY);

  store.dispatch(editEntity({ data }));
};
